import { Button } from "@/components/ui/button"
import { Users } from "lucide-react"
import { useCursor } from "../../contexts/cursor"
import { cn } from "../../lib/utils"

export function CursorButton() {
	const {
		connectionState,
		latency,
		peers,
		remoteCursors,
		debugMode,
		setDebugMode,
	} = useCursor()

	const connectionColors: Record<string, string> = {
		disconnected: "text-red-500",
		connecting: "text-yellow-500",
		signaling: "text-yellow-500",
		connected: "text-green-500",
		fallback: "text-orange-500",
	}

	const hasPeers = peers.size > 0
	const hasCursors = remoteCursors.size > 0

	return (
		<div className="flex items-end gap-2">
			{/* Inline debug info - shown when debug mode is enabled */}
			{debugMode && (
				<div className="mb-2 rounded-lg border border-border bg-background/95 backdrop-blur-sm px-3 py-2 text-xs shadow-md">
					<div className="space-y-1.5">
						<div className="flex items-center gap-3">
							<span className="text-muted-foreground">State:</span>
							<span
								className={cn("font-medium", connectionColors[connectionState])}
							>
								{connectionState}
							</span>
						</div>
						<div className="flex items-center gap-3">
							<span className="text-muted-foreground">Latency:</span>
							<span className="font-medium">
								{latency !== null ? `${latency}ms` : "-"}
							</span>
						</div>
						<div className="flex items-center gap-3">
							<span className="text-muted-foreground">Peers:</span>
							<span className="font-medium">{peers.size}</span>
						</div>
						<div className="flex items-center gap-3">
							<span className="text-muted-foreground">Cursors:</span>
							<span className="font-medium">{remoteCursors.size}</span>
						</div>
					</div>

					{hasPeers && (
						<div className="mt-2 border-t border-border pt-2">
							<div className="mb-1.5 text-xs font-medium text-muted-foreground">
								Peers:
							</div>
							<div className="space-y-1">
								{Array.from(peers.values()).map((peer) => (
									<div
										key={peer.connectionId}
										className="flex items-center gap-2"
									>
										<div
											className="h-2 w-2 rounded-full"
											style={{ backgroundColor: peer.color }}
										/>
										<span className="text-xs">{peer.name}</span>
									</div>
								))}
							</div>
						</div>
					)}

					{hasCursors && (
						<div className="mt-2 border-t border-border pt-2">
							<div className="mb-1.5 text-xs font-medium text-muted-foreground">
								Cursors:
							</div>
							<div className="space-y-1">
								{Array.from(remoteCursors.values()).map((cursor) => (
									<div
										key={cursor.connectionId}
										className="rounded bg-muted/50 p-1.5 text-[10px]"
									>
										<div className="flex justify-between">
											<span className="text-muted-foreground">State:</span>
											<span className="text-blue-500">{cursor.state}</span>
										</div>
										{cursor.draggedNodes && cursor.draggedNodes.length > 0 && (
											<div className="flex justify-between">
												<span className="text-muted-foreground">Dragging:</span>
												<span className="text-yellow-500">
													{cursor.draggedNodes.length} node(s)
												</span>
											</div>
										)}
										{cursor.connectingFrom && (
											<div className="flex justify-between">
												<span className="text-muted-foreground">
													Connecting:
												</span>
												<span className="text-green-500">
													{cursor.connectingFrom.nodeId} (
													{cursor.connectingFrom.handleType})
												</span>
											</div>
										)}
										{cursor.selectionBox && (
											<div className="flex justify-between">
												<span className="text-muted-foreground">
													Selection:
												</span>
												<span className="text-purple-500">
													{cursor.selectionBox.mode} (
													{Math.round(cursor.selectionBox.width)}x
													{Math.round(cursor.selectionBox.height)})
												</span>
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Cursor button - positioned to the left of zoom controls */}
			<Button
				variant="outline"
				size="icon"
				onClick={() => setDebugMode(!debugMode)}
				className={cn(
					"h-8 w-8 bg-background/95 backdrop-blur-sm shadow-md",
					debugMode && "bg-accent",
				)}
				title={debugMode ? "Hide cursor debug info" : "Show cursor debug info"}
			>
				<Users className="h-4 w-4" />
			</Button>
		</div>
	)
}
